import { useState, useCallback, useEffect } from 'react'
import { GridPaginationModel } from '@mui/x-data-grid'
import { Stack, Button, Typography } from '@mui/material'
import InfoDialog from '../../../components/InfoDiaglog'
import React from 'react'

/**
 * Props for usePagination hook
 */
export interface UsePaginationProps {
  /**
   * Initial pagination model
   */
  initialModel?: GridPaginationModel
  /**
   * Callback when pagination changes
   */
  onPageChange?: (model: GridPaginationModel) => void
  /**
   * Whether there are selected items on the current page
   */
  hasSelectedItems?: boolean
  /**
   * Whether to show selection limitation warning when changing pages
   */
  showSelectionLimitationWarning?: boolean
  /**
   * Fetch function to call when pagination changes
   */
  fetchData?: (params: { page: number; pageSize: number }) => void
}

/**
 * Hook return type
 */
export interface UsePaginationReturn {
  /**
   * Current pagination model
   */
  paginationModel: GridPaginationModel
  /**
   * Handler for pagination model change
   */
  handlePaginationModelChange: (newModel: GridPaginationModel) => void
  /**
   * Set pagination model directly
   */
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>
}

/**
 * Hook for managing pagination in DataGrid
 */
const usePagination = ({
  initialModel = { page: 0, pageSize: 10 },
  onPageChange,
  hasSelectedItems = false,
  showSelectionLimitationWarning = true,
  fetchData
}: UsePaginationProps): UsePaginationReturn => {
  // State for pagination model
  const [paginationModel, setPaginationModel] =
    useState<GridPaginationModel>(initialModel)

  // Handle pagination model change
  const handlePaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      // Show selection limitation warning when changing pages
      const isPageChange = newModel.page !== paginationModel.page

      // Only show warning if enabled, has selected items, and changing page
      if (showSelectionLimitationWarning && hasSelectedItems && isPageChange) {
        const dontShowSelectionLimitation = localStorage.getItem(
          'dontShowSelectionLimitation'
        )

        if (!dontShowSelectionLimitation) {
          // Handler for "Don't show again" button
          const handleDontShowAgain = () => {
            InfoDialog.close()
            localStorage.setItem('dontShowSelectionLimitation', 'true')
          }

          InfoDialog.open({
            type: 'info',
            title: 'Selection Limitations',
            message:
              'Selected rows do not persist across page changes and will be deselected when you navigate to next page.',
            btnSlot: (
              <Stack direction='row' spacing={2} sx={{ flexDirection: 'row' }}>
                <Button variant='text' onClick={handleDontShowAgain}>
                  <Typography
                    color='primary'
                    sx={{ fontSize: 14, fontWeight: 'bold' }}>
                    Don&apos;t show Again
                  </Typography>
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => InfoDialog.close()}
                  sx={{ minWidth: 130 }}>
                  OK
                </Button>
              </Stack>
            )
          })
        }
      }

      // Update pagination model
      setPaginationModel(newModel)

      // Call onPageChange callback
      onPageChange && onPageChange(newModel)

      // Fetch data if provided
      fetchData && fetchData(newModel)
    },
    [
      paginationModel,
      hasSelectedItems,
      showSelectionLimitationWarning,
      onPageChange,
      fetchData
    ]
  )

  // Initialize with fetch if fetchData is provided
  useEffect(() => {
    if (fetchData) {
      fetchData(initialModel)
    }
  }, [fetchData, initialModel])

  return {
    paginationModel,
    handlePaginationModelChange,
    setPaginationModel
  }
}

export default usePagination
